import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import { createHttpLink } from 'apollo-link-http';
import fetch from 'unfetch';
import { ApolloLink, from } from 'apollo-link';
import ActionCable from 'actioncable';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import rollbar from '@/plugins/rollbar';
import get from 'lodash/get';
import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { getUserToken, removeToken, storeToken } from '@/utilities/tokenTools';
// Install the vue plugin
Vue.use(VueApollo);

const socketUrl = generateWsUrl();
const cable = ActionCable.createConsumer(socketUrl);

const hasSubscriptionOperation = ({ query: { definitions } }: { query: { definitions: any }}) => {
  return definitions.some(
    ({ kind, operation }: { kind: any; operation: any }) => {
      return kind === 'OperationDefinition' && operation === 'subscription';
    });
};

const errorLink = onError(({ graphQLErrors, response, networkError }: any) => {
  if (get(response, 'status') === 401 || get(networkError, 'statusCode') === 401) {
    return invalidateUserToken();
  }

  if (get(graphQLErrors, '[0].extensions.code') === 'resource_not_found') {
    if (process.env.NODE_ENV === 'production') {
      rollbar.warning('404 redirect');
    }
    return;
  }

  if (graphQLErrors[0]?.message !== 'Login failed') {
    rollbar.error(graphQLErrors[0]?.extensions?.code || graphQLErrors[0]?.message, graphQLErrors);
  }
});

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      'X-Auth-Token': getUserToken(),
      'X-Auth-Role': 'operator'
    }
  });
  if (forward) {
    return forward(operation);
  }

  return null;
});

const httpLink = createHttpLink({
  uri: process.env.VUE_APP_GRAPHQL_API,
  fetch: fetch
});

const link = ApolloLink.split(
  hasSubscriptionOperation,
  new ActionCableLink({ cable }),
  httpLink
);

export const client = new ApolloClient({
  link: from([errorLink, authLink, link]),
  cache: new InMemoryCache({
    addTypename: false,
    fragmentMatcher: {
      match: ({ id }, typeCond, context) => !!context.store.get(id)
    }
  }),
  defaultOptions: { query: { fetchPolicy: 'no-cache' } }
});

// Manually call this when user log in
export function onLogin(token: string) {
  storeToken(token);
  cable.url = generateWsUrl();
  cable.connection.reopen();
}

// Manually call this when user log out
export async function onLogout() {
  await invalidateUserToken();
}

export const vueApolloProvider = new VueApollo({
  defaultClient: client
});

function generateWsUrl() {
  return `${process.env.VUE_APP_WEBSOCKET_ADDRESS}?token=${getUserToken()}`;
}

function invalidateUserToken() {
  removeToken();
  cable.url = generateWsUrl();
  cable.connection.close();
  window.location.href = '/';
}
